// What a line of chat says and how it is said: who it is for, whether it is
// an emote, and which of its words are links. Nothing here touches the
// page, so it can all be checked without one; `chatview` draws the result.

import type { Vec3 } from "@/net/codec";
import { ChatType } from "@/net/messages";
import { parsePlace } from "@/world/tracker";

/**
 * The rest of an emote after the speaker's name, or null for a line that
 * is not one. "/me waves" reads "Name waves" and "/me's hat is red" reads
 * "Name's hat is red", as the desktop viewer has it; "/meow" is a word.
 */
export function emoteTail(text: string): string | null {
  const m = /^\/me(?=$|[\s'’])/i.exec(text);
  if (!m) return null;
  const rest = text.slice(m[0].length);
  if (rest.startsWith("'") || rest.startsWith("’")) return rest;
  const said = rest.trim();
  return said ? ` ${said}` : "";
}

/** A line about to be said nearby: on which channel, and how loudly. */
export interface Outgoing {
  text: string;
  channel: number;
  type: ChatType;
}

/**
 * How loudly the keys say a line: Ctrl with Enter shouts and Shift with
 * Enter whispers, as in the desktop viewer.
 */
export function typeForKeys(keys: { ctrlKey: boolean; shiftKey: boolean }): ChatType {
  if (keys.ctrlKey) return ChatType.Shout;
  if (keys.shiftKey) return ChatType.Whisper;
  return ChatType.Say;
}

/**
 * What a typed line asks for. "/5 open" says "open" on channel 5;
 * "/shout" and "/whisper" in front of a line say it that way; anything
 * else, an emote included, is said aloud as it was typed.
 */
export function parseOutgoing(raw: string, type: ChatType): Outgoing | null {
  const text = raw.trim();
  if (!text) return null;
  const channel = /^\/(-?\d+)(?:\s+([\s\S]*))?$/.exec(text);
  if (channel) {
    const body = (channel[2] ?? "").trim();
    return body ? { text: body, channel: Number(channel[1]), type } : null;
  }
  const loud = /^\/(shout|whisper)(?:\s+([\s\S]*))?$/i.exec(text);
  if (loud) {
    const body = (loud[2] ?? "").trim();
    if (!body) return null;
    return { text: body, channel: 0, type: loud[1].toLowerCase() === "shout" ? ChatType.Shout : ChatType.Whisper };
  }
  return { text, channel: 0, type };
}

/**
 * Whether what is in the chat box says the avatar is talking. A line for a
 * channel is a command to something, not talk, and so is anything else
 * behind a slash but an emote or a shouted or whispered line.
 */
export function showsTyping(value: string): boolean {
  const text = value.trimStart();
  if (!text) return false;
  if (!text.startsWith("/")) return true;
  return emoteTail(text) !== null || /^\/(shout|whisper)\b/i.test(text);
}

/** One run of a line: plain words, a link out of the world, or a place in it. */
export type Piece =
  | { kind: "text"; text: string }
  | { kind: "url"; text: string; href: string }
  | { kind: "place"; text: string; regionName: string; position: Vec3 };

const LINK = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;
/** Said after a link more often than as the end of one. */
const TRAILING = /[.,;:!?'"’”]+$/;

/**
 * Cut a line into words and links. A link to a place on this grid — the
 * viewer's own address with `?place=Region/x/y/z`, which is what the map
 * and landmarks copy — is a place, to be shown on the map; any other web
 * address is a link, only ever http or https.
 */
export function pieces(text: string, origin: string): Piece[] {
  const out: Piece[] = [];
  let from = 0;
  const plain = (to: number) => {
    if (to > from) out.push({ kind: "text", text: text.slice(from, to) });
  };
  for (const m of text.matchAll(LINK)) {
    let raw = m[0];
    const trailing = TRAILING.exec(raw);
    if (trailing) raw = raw.slice(0, raw.length - trailing[0].length);
    // A closing bracket belongs to the link only if it opened one.
    while (raw.endsWith(")") && (raw.match(/\(/g)?.length ?? 0) < (raw.match(/\)/g)?.length ?? 0)) raw = raw.slice(0, -1);
    const at = m.index ?? 0;
    const piece = linkPiece(raw, origin);
    if (!piece) continue;
    plain(at);
    out.push(piece);
    from = at + raw.length;
  }
  plain(text.length);
  return out;
}

function linkPiece(raw: string, origin: string): Piece | null {
  const href = /^www\./i.test(raw) ? `https://${raw}` : raw;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const place = url.origin === origin ? parsePlace(url.searchParams.get("place") ?? "") : null;
  if (place) return { kind: "place", text: raw, regionName: place.regionName, position: place.position };
  return { kind: "url", text: raw, href: url.href };
}
